import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import axios from 'axios';
import { toast } from 'sonner';
import {
    Plus, Search, Filter, DollarSign, Calendar, AlertTriangle,
    ChevronRight, Sparkles, GripVertical, X, MoreHorizontal, List, BarChart3
} from 'lucide-react';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '../components/ui/dialog';
import SortableHeader, { useTableSort, useColumnFilters, usePagination, TablePagination } from '../components/table/SortableHeader';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const STAGES = ['Prospect', 'Qualified', 'Proposal Sent', 'Negotiation', 'Closed Won', 'Closed Lost'];
const STAGE_COLORS = {
    Prospect: { bg: 'bg-blue-50', border: 'border-blue-200', text: 'text-blue-700', dot: 'bg-blue-500' },
    Qualified: { bg: 'bg-stone-100', border: 'border-stone-300', text: 'text-stone-900', dot: 'bg-stone-1000' },
    'Proposal Sent': { bg: 'bg-amber-50', border: 'border-amber-200', text: 'text-amber-700', dot: 'bg-amber-500' },
    Negotiation: { bg: 'bg-violet-50', border: 'border-violet-200', text: 'text-violet-700', dot: 'bg-violet-500' },
    'Closed Won': { bg: 'bg-emerald-50', border: 'border-emerald-200', text: 'text-emerald-700', dot: 'bg-emerald-500' },
    'Closed Lost': { bg: 'bg-red-50', border: 'border-red-200', text: 'text-red-700', dot: 'bg-red-500' },
};

function DealModal({ open, onClose, deal, accounts, onSave }) {
    const { getAuthHeader } = useAuth();
    const [form, setForm] = useState({ title: '', account_id: '', account_name: '', stage: 'Prospect', value: '', probability: 10, expected_close_date: '', description: '', source: '', tags: [] });
    const [loading, setLoading] = useState(false);

    useEffect(() => {
        if (deal) setForm({ ...deal, value: deal.value || '' });
        else setForm({ title: '', account_id: '', account_name: '', stage: 'Prospect', value: '', probability: 10, expected_close_date: '', description: '', source: '', tags: [] });
    }, [deal]);

    const handleSave = async () => {
        if (!form.title) return toast.error('Deal title required');
        setLoading(true);
        try {
            const payload = { ...form, value: parseFloat(form.value) || 0 };
            // Auto-set account_name from account_id
            if (payload.account_id) {
                const acc = accounts.find(a => a.account_id === payload.account_id);
                if (acc) payload.account_name = acc.name;
            }
            let res;
            if (deal) res = await axios.put(`${API}/deals/${deal.deal_id}`, payload, { headers: getAuthHeader() });
            else res = await axios.post(`${API}/deals`, payload, { headers: getAuthHeader() });
            toast.success(deal ? 'Deal updated' : 'Deal created');
            onSave(res.data);
            onClose();
        } catch { toast.error('Failed to save deal'); }
        finally { setLoading(false); }
    };

    const PROB_MAP = { Prospect: 10, Qualified: 25, 'Proposal Sent': 50, Negotiation: 75, 'Closed Won': 100, 'Closed Lost': 0 };

    return (
        <Dialog open={open} onOpenChange={onClose}>
            <DialogContent className="max-w-xl">
                <DialogHeader>
                    <DialogTitle style={{ fontFamily: 'Manrope, sans-serif' }}>{deal ? 'Edit Deal' : 'New Deal'}</DialogTitle>
                </DialogHeader>
                <div className="grid grid-cols-2 gap-4 mt-2">
                    <div className="col-span-2">
                        <label className="block text-xs font-semibold text-stone-500 uppercase tracking-wider mb-1">Deal Title *</label>
                        <input value={form.title} onChange={e => setForm({ ...form, title: e.target.value })} placeholder="Enterprise License Deal"
                            data-testid="deal-title-input"
                            className="w-full px-3 py-2 text-sm border border-stone-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-stone-900/20 focus:border-stone-400" />
                    </div>
                    <div>
                        <label className="block text-xs font-semibold text-stone-500 uppercase tracking-wider mb-1">Company</label>
                        <select value={form.account_id || ''} onChange={e => setForm({ ...form, account_id: e.target.value })} data-testid="deal-account-select"
                            className="w-full px-3 py-2 text-sm border border-stone-200 rounded-lg bg-white focus:outline-none">
                            <option value="">Select account...</option>
                            {accounts.map(a => <option key={a.account_id} value={a.account_id}>{a.name}</option>)}
                        </select>
                    </div>
                    <div>
                        <label className="block text-xs font-semibold text-stone-500 uppercase tracking-wider mb-1">Stage</label>
                        <select value={form.stage} onChange={e => setForm({ ...form, stage: e.target.value, probability: PROB_MAP[e.target.value] })} data-testid="deal-stage-select"
                            className="w-full px-3 py-2 text-sm border border-stone-200 rounded-lg bg-white focus:outline-none">
                            {STAGES.map(s => <option key={s}>{s}</option>)}
                        </select>
                    </div>
                    <div>
                        <label className="block text-xs font-semibold text-stone-500 uppercase tracking-wider mb-1">Deal Value ($)</label>
                        <input type="number" value={form.value} onChange={e => setForm({ ...form, value: e.target.value })} placeholder="50000"
                            data-testid="deal-value-input"
                            className="w-full px-3 py-2 text-sm border border-stone-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-stone-900/20 focus:border-stone-400" />
                    </div>
                    <div>
                        <label className="block text-xs font-semibold text-stone-500 uppercase tracking-wider mb-1">Close Date</label>
                        <input type="date" value={form.expected_close_date || ''} onChange={e => setForm({ ...form, expected_close_date: e.target.value })}
                            data-testid="deal-close-date-input"
                            className="w-full px-3 py-2 text-sm border border-stone-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-stone-900/20" />
                    </div>
                    <div>
                        <label className="block text-xs font-semibold text-stone-500 uppercase tracking-wider mb-1">Source</label>
                        <select value={form.source || ''} onChange={e => setForm({ ...form, source: e.target.value })} data-testid="deal-source-select"
                            className="w-full px-3 py-2 text-sm border border-stone-200 rounded-lg bg-white focus:outline-none">
                            <option value="">Select...</option>
                            {['inbound', 'outbound', 'referral', 'partner'].map(s => <option key={s} className="capitalize">{s}</option>)}
                        </select>
                    </div>
                    <div>
                        <label className="block text-xs font-semibold text-stone-500 uppercase tracking-wider mb-1">Probability (%)</label>
                        <input type="number" min="0" max="100" value={form.probability} onChange={e => setForm({ ...form, probability: parseInt(e.target.value) })}
                            className="w-full px-3 py-2 text-sm border border-stone-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-stone-900/20" />
                    </div>
                    <div className="col-span-2">
                        <label className="block text-xs font-semibold text-stone-500 uppercase tracking-wider mb-1">Description</label>
                        <textarea value={form.description || ''} onChange={e => setForm({ ...form, description: e.target.value })} rows={2}
                            className="w-full px-3 py-2 text-sm border border-stone-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-stone-900/20" />
                    </div>
                </div>
                <div className="flex justify-end gap-3 mt-4">
                    <button onClick={onClose} className="px-4 py-2 text-sm border border-stone-200 rounded-lg hover:bg-stone-50 text-stone-700">Cancel</button>
                    <button onClick={handleSave} disabled={loading} data-testid="deal-save-btn"
                        className="px-4 py-2 text-sm bg-stone-900 text-white rounded-lg hover:bg-stone-800 disabled:opacity-50">
                        {loading ? 'Saving...' : 'Save Deal'}
                    </button>
                </div>
            </DialogContent>
        </Dialog>
    );
}

function DealCard({ deal, onEdit, onDragStart }) {
    const navigate = useNavigate();
    const formatVal = v => v >= 1e6 ? `$${(v/1e6).toFixed(1)}M` : v >= 1e3 ? `$${(v/1e3).toFixed(0)}K` : `$${v}`;

    return (
        <div
            className="bg-white rounded-lg border border-stone-200 p-3 cursor-pointer hover:border-stone-400 hover:shadow-md transition-all group relative"
            draggable
            onDragStart={e => onDragStart(e, deal)}
            onClick={() => navigate(`/deals/${deal.deal_id}`)}
            data-testid={`deal-card-${deal.deal_id}`}
            data-nav-to="DealDetail"
        >
            <div className="flex items-start justify-between mb-2">
                <p className="text-sm font-medium text-stone-900 leading-snug pr-2" data-field="deal.title">{deal.title}</p>
                <button onClick={e => { e.stopPropagation(); onEdit(deal); }} className="p-1 rounded hover:bg-stone-100 opacity-0 group-hover:opacity-100 transition-opacity flex-shrink-0">
                    <MoreHorizontal className="w-3.5 h-3.5 text-stone-400" />
                </button>
            </div>
            {deal.account_name && <p className="text-xs text-stone-400 mb-2">{deal.account_name}</p>}
            <div className="flex items-center justify-between">
                <span className="text-sm font-semibold text-stone-700">{deal.value ? formatVal(deal.value) : '—'}</span>
                {deal.ai_risk_score !== undefined && deal.stage !== 'Closed Won' && deal.stage !== 'Closed Lost' && (
                    <span className={`text-xs px-1.5 py-0.5 rounded-full font-medium flex items-center gap-1 ${deal.ai_risk_score > 60 ? 'risk-high' : deal.ai_risk_score > 30 ? 'risk-medium' : 'risk-low'}`}>
                        {deal.ai_risk_score > 60 && <AlertTriangle className="w-3 h-3" />}
                        {deal.ai_risk_score}
                    </span>
                )}
            </div>
            {deal.expected_close_date && (
                <p className="text-xs text-stone-400 mt-1.5 flex items-center gap-1">
                    <Calendar className="w-3 h-3" />{deal.expected_close_date}
                </p>
            )}
        </div>
    );
}

export default function Pipeline() {
    const { getAuthHeader } = useAuth();
    const navigate = useNavigate();
    const [deals, setDeals] = useState([]);
    const [accounts, setAccounts] = useState([]);
    const [loading, setLoading] = useState(true);
    const [showModal, setShowModal] = useState(false);
    const [editDeal, setEditDeal] = useState(null);
    const [view, setView] = useState('kanban');
    const [draggedDeal, setDraggedDeal] = useState(null);
    const [dragOverStage, setDragOverStage] = useState(null);
    const [search, setSearch] = useState('');

    useEffect(() => { loadData(); }, []);

    const loadData = async () => {
        try {
            const [dRes, aRes] = await Promise.all([
                axios.get(`${API}/deals`, { headers: getAuthHeader() }),
                axios.get(`${API}/accounts`, { headers: getAuthHeader() }),
            ]);
            setDeals(Array.isArray(dRes.data) ? dRes.data : (dRes.data?.data || []));
            setAccounts(Array.isArray(aRes.data) ? aRes.data : (aRes.data?.data || []));
        } catch {} finally { setLoading(false); }
    };

    const handleSave = (saved) => {
        setDeals(prev => {
            const idx = prev.findIndex(d => d.deal_id === saved.deal_id);
            if (idx >= 0) { const u = [...prev]; u[idx] = saved; return u; }
            return [saved, ...prev];
        });
    };

    // Drag & drop
    const handleDragStart = (e, deal) => {
        setDraggedDeal(deal);
        e.dataTransfer.effectAllowed = 'move';
    };
    const handleDragOver = (e, stage) => {
        e.preventDefault();
        setDragOverStage(stage);
    };
    const handleDrop = async (e, stage) => {
        e.preventDefault();
        if (!draggedDeal || draggedDeal.stage === stage) { setDraggedDeal(null); setDragOverStage(null); return; }
        try {
            const PROB_MAP = { Prospect: 10, Qualified: 25, 'Proposal Sent': 50, Negotiation: 75, 'Closed Won': 100, 'Closed Lost': 0 };
            const res = await axios.put(`${API}/deals/${draggedDeal.deal_id}`, { stage, probability: PROB_MAP[stage] }, { headers: getAuthHeader() });
            setDeals(prev => prev.map(d => d.deal_id === draggedDeal.deal_id ? res.data : d));
            toast.success(`Moved to ${stage}`);
        } catch { toast.error('Failed to update stage'); }
        finally { setDraggedDeal(null); setDragOverStage(null); }
    };

    const filteredDeals = search ? deals.filter(d => d.title?.toLowerCase().includes(search.toLowerCase()) || d.account_name?.toLowerCase().includes(search.toLowerCase())) : deals;
    const dealsByStage = (stage) => filteredDeals.filter(d => d.stage === stage);
    const formatVal = v => v >= 1e6 ? `$${(v/1e6).toFixed(1)}M` : v >= 1e3 ? `$${(v/1e3).toFixed(0)}K` : `$${v}`;

    // List view sort + column filter
    const DEAL_COLS = [
        { key: 'title', label: 'Deal' },
        { key: 'stage', label: 'Stage' },
        { key: 'value', label: 'Value' },
        { key: 'expected_close_date', label: 'Close Date' },
        { key: 'ai_risk_score', label: 'Risk' },
        { key: 'probability', label: 'Probability' },
    ];
    const { columnFilters: dealColFilters, setColumnFilter: setDealColFilter, filteredData: colFilteredDeals } = useColumnFilters(filteredDeals);
    const { sortColumn: dealSortCol, sortDirection: dealSortDir, handleSort: handleDealSort, sortedData: sortedDeals } = useTableSort(colFilteredDeals, { numericKeys: ['value', 'ai_risk_score', 'probability'] });
    const { page: dealPage, pageSize: dealPageSize, setPage: setDealPage, setPageSize: setDealPageSize, pageData: pagedDeals, totalPages: dealTotalPages, total: dealTotal } = usePagination(sortedDeals, 25);
    const totalPipeline = deals.filter(d => !['Closed Won', 'Closed Lost'].includes(d.stage)).reduce((s, d) => s + (d.value || 0), 0);

    return (
        <div className="space-y-4">
            {/* Toolbar */}
            <div className="flex items-center justify-between gap-4 flex-wrap">
                <div className="flex items-center gap-3 flex-1 flex-wrap">
                    <div className="relative min-w-48 max-w-sm flex-1">
                        <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-stone-400" />
                        <input type="text" placeholder="Search deals..." value={search} onChange={e => setSearch(e.target.value)}
                            data-testid="pipeline-search-input"
                            data-field="deal.search_query"
                            className="w-full pl-9 pr-4 py-2 text-sm border border-stone-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-stone-900/20 bg-white" />
                    </div>
                    <div className="flex rounded-lg border border-stone-200 overflow-hidden bg-white">
                        <button onClick={() => setView('kanban')} data-testid="kanban-view-btn"
                            data-action="pipeline.view_kanban"
                            className={`px-3 py-2 text-sm flex items-center gap-2 transition-colors ${view === 'kanban' ? 'bg-stone-900 text-white' : 'text-stone-600 hover:bg-stone-50'}`}>
                            <GripVertical className="w-4 h-4" />Kanban
                        </button>
                        <button onClick={() => setView('list')} data-testid="list-view-btn"
                            data-action="pipeline.view_list"
                            className={`px-3 py-2 text-sm flex items-center gap-2 transition-colors ${view === 'list' ? 'bg-stone-900 text-white' : 'text-stone-600 hover:bg-stone-50'}`}>
                            <List className="w-4 h-4" />List
                        </button>
                    </div>
                </div>
                <div className="flex items-center gap-3">
                    <div className="text-sm text-stone-600">
                        Pipeline: <span className="font-bold text-stone-900" data-field="deal.total_pipeline_value">{formatVal(totalPipeline)}</span>
                    </div>
                    <button onClick={() => { setEditDeal(null); setShowModal(true); }} data-testid="new-deal-btn"
                        data-action="deal.create"
                        className="flex items-center gap-2 px-4 py-2 bg-stone-900 text-white text-sm font-medium rounded-lg hover:bg-stone-800 shadow-sm">
                        <Plus className="w-4 h-4" /> New Deal
                    </button>
                </div>
            </div>

            {/* Kanban Board */}
            {view === 'kanban' && (
                <div className="overflow-x-auto pb-4">
                    <div className="flex gap-4 min-w-max">
                        {STAGES.map(stage => {
                            const stageDeals = dealsByStage(stage);
                            const stageVal = stageDeals.reduce((s, d) => s + (d.value || 0), 0);
                            const colors = STAGE_COLORS[stage];
                            const isOver = dragOverStage === stage;
                            return (
                                <div key={stage}
                                    className={`w-64 flex flex-col bg-stone-50 rounded-xl border ${isOver ? 'border-stone-300 bg-stone-100/30' : 'border-stone-200'} transition-colors`}
                                    onDragOver={e => handleDragOver(e, stage)}
                                    onDrop={e => handleDrop(e, stage)}
                                    data-testid={`kanban-col-${stage.replace(/\s+/g, '-').toLowerCase()}`}
                                >
                                    {/* Column header */}
                                    <div className="p-3 border-b border-stone-200">
                                        <div className="flex items-center justify-between mb-1">
                                            <div className="flex items-center gap-2">
                                                <div className={`w-2 h-2 rounded-full ${colors.dot}`} />
                                                <span className="text-xs font-semibold text-stone-700">{stage}</span>
                                            </div>
                                            <span className={`text-xs font-bold px-1.5 py-0.5 rounded-full ${colors.bg} ${colors.text}`}>
                                                {stageDeals.length}
                                            </span>
                                        </div>
                                        <p className="text-xs text-stone-400 font-medium">{formatVal(stageVal)}</p>
                                    </div>
                                    {/* Cards */}
                                    <div className="p-2 flex-1 space-y-2 kanban-col">
                                        {stageDeals.map(deal => (
                                            <DealCard key={deal.deal_id} deal={deal} onEdit={d => { setEditDeal(d); setShowModal(true); }} onDragStart={handleDragStart} />
                                        ))}
                                    </div>
                                    <button onClick={() => { setEditDeal({ stage }); setShowModal(true); }}
                                        className="m-2 p-2 rounded-lg border border-dashed border-stone-300 text-stone-400 hover:text-stone-700 hover:border-stone-400 text-xs flex items-center justify-center gap-1 transition-colors">
                                        <Plus className="w-3.5 h-3.5" /> Add deal
                                    </button>
                                </div>
                            );
                        })}
                    </div>
                </div>
            )}

            {/* List View */}
            {view === 'list' && (
                <div className="bg-white rounded-xl border border-stone-200 overflow-hidden" data-testid="pipeline-list-view">
                    <table className="w-full">
                        <thead>
                            <tr className="bg-stone-50 border-b border-stone-200">
                                {DEAL_COLS.map(col => (
                                    <SortableHeader
                                        key={col.key}
                                        label={col.label}
                                        columnKey={col.key}
                                        sortColumn={dealSortCol}
                                        sortDirection={dealSortDir}
                                        onSort={handleDealSort}
                                        filterValue={dealColFilters[col.key]}
                                        onFilter={setDealColFilter}
                                        allData={filteredDeals}
                                    />
                                ))}
                                <th className="px-4 py-3" />
                            </tr>
                        </thead>
                        <tbody className="divide-y divide-stone-100">
                            {pagedDeals.map(d => {
                                const colors = STAGE_COLORS[d.stage];
                                return (
                                    <tr key={d.deal_id} className="hover:bg-stone-50 cursor-pointer transition-colors group" onClick={() => navigate(`/deals/${d.deal_id}`)}>
                                        <td className="px-4 py-3">
                                            <p className="text-sm font-medium text-stone-900">{d.title}</p>
                                            <p className="text-xs text-stone-400">{d.account_name}</p>
                                        </td>
                                        <td className="px-4 py-3">
                                            <span className={`text-xs px-2 py-0.5 rounded-full font-medium ${colors?.bg} ${colors?.text}`}>{d.stage}</span>
                                        </td>
                                        <td className="px-4 py-3 font-medium text-sm text-stone-700">{d.value ? formatVal(d.value) : '—'}</td>
                                        <td className="px-4 py-3 text-sm text-stone-500">{d.expected_close_date || '—'}</td>
                                        <td className="px-4 py-3">
                                            {d.ai_risk_score !== undefined ? (
                                                <span className={`text-xs px-1.5 py-0.5 rounded-full font-medium ${d.ai_risk_score > 60 ? 'risk-high' : d.ai_risk_score > 30 ? 'risk-medium' : 'risk-low'}`}>
                                                    {d.ai_risk_score}
                                                </span>
                                            ) : '—'}
                                        </td>
                                        <td className="px-4 py-3">
                                            <div className="flex items-center gap-2">
                                                <div className="w-16 h-1.5 bg-stone-100 rounded-full overflow-hidden">
                                                    <div className="h-full bg-stone-1000 rounded-full" style={{ width: `${d.probability || 0}%` }} />
                                                </div>
                                                <span className="text-xs text-stone-500">{d.probability || 0}%</span>
                                            </div>
                                        </td>
                                        <td className="px-4 py-3">
                                            <button onClick={e => { e.stopPropagation(); setEditDeal(d); setShowModal(true); }} className="p-1.5 rounded-lg hover:bg-stone-100 opacity-0 group-hover:opacity-100 transition-opacity">
                                                <MoreHorizontal className="w-4 h-4 text-stone-400" />
                                            </button>
                                        </td>
                                    </tr>
                                );
                            })}
                        </tbody>
                    </table>
                    <TablePagination
                        page={dealPage}
                        totalPages={dealTotalPages}
                        total={dealTotal}
                        pageSize={dealPageSize}
                        onPageChange={setDealPage}
                        onPageSizeChange={setDealPageSize}
                    />
                </div>
            )}

            <DealModal open={showModal} onClose={() => { setShowModal(false); setEditDeal(null); }} deal={editDeal} accounts={accounts} onSave={handleSave} />
        </div>
    );
}
